import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
  Linking,
  TouchableOpacity,
  Alert,
} from "react-native";
import dayjs from "dayjs";
import Text from "@components/CustomText";
import { useDispatch, useSelector } from "react-redux";
import SearchBar from "@components/SearchBar";
import colors from "@constants/color";
import NoOrdersGraphic from "@assets/svg/NoOrdersGraphic";
import metrics, { normalize } from "@constants/metrics";
import CloseIcon from "@assets/svg/CloseIcon";
import t from "@constants/localization";

import Share from "react-native-share";

// Events
import analytics from "@services/analytics";
import { MY_ORDERS_EVENTS } from "@constants/analyticsEventsCT";
import testIds from "@constants/testIds";
import { useDebounce } from "../../utils/debounce";
import { actions as returnOrderActions } from "../../reducers/returnOrdersReducer";
import ReturnOrderCard from "./View/ReturnOrderCard";
import CustomModal from "./CustomModal";

import OrderCard from "./View/OrderCard";
import { actions as orderActions } from "../../reducers/orderReducer";

const PATH = "screens/OrdersList/index";
const PATH2 = "WhatsAppAlert";

const keyExtractor = (item, index) => String(index);

function OrderList(props) {
  const {
    route: { params },
  } = props;

  const [query, setQuery] = useState("");
  const dispatch = useDispatch();
  const { allOrders, loading, totalCount } = useSelector((s) => s.orders);
  const {
    allReturnOrders,
    loading: returnOrdersloading,
    totalCount: totalReturnOrderCount,
    modalValues,
  } = useSelector((s) => s.returnOrders);
  const navigation = useNavigation();

  const debouncedQuery = useDebounce(query, 300);
  const [isOrders, setIsOrders] = useState(true);
  const [endReached, setEndReached] = useState(false);
  const [ordersList, setOrdersList] = useState([]);

  useEffect(() => {
    if (allOrders.length === totalCount) {
      setEndReached(true);
    }
    if (params?.fromShop) {
      const results = allOrders.filter((order) => {
        if (order?.created_at.substr(2, 8) === dayjs().format("DD/MM/YY")) {
          return order;
        }
      });
      setOrdersList(results);
    } else {
      setOrdersList(allOrders);
    }
  }, [allOrders, params?.fromShop, totalCount]);

  useEffect(() => {
    if (isOrders) {
      dispatch(orderActions.getAllOrdersReq({ query: debouncedQuery }));
    } else {
      dispatch(
        returnOrderActions.fetchReturnOrderListRequest({
          query: debouncedQuery,
        }),
      );
    }
  }, [dispatch, debouncedQuery, isOrders]);

  const _renderItem = ({ item, index }) => {
    if (isOrders) {
      return (
        <OrderCard
          item={item}
          index={index}
          navigateToDetails={navigateToDetails}
          navigateToRating={navigateToRating}
          onWaShare={onWaShare}
        />
      );
    } else {
      return (
        <ReturnOrderCard item={item} navigateToDetails={navigateToDetails} />
      );
    }
  };

  const onWaShare = async (share_message, user_phone, order_id) => {
    const { isInstalled: haveWhatsApp } =
      await Share.isPackageInstalled("com.whatsapp");
    if (!haveWhatsApp) {
      Alert.alert(
        t(`${PATH2}/title`),
        t(`${PATH2}/message`),
        [
          {
            text: t(`${PATH2}/onCloseText`),
            onClose: () => {},
          },
        ],
        { cancelable: true },
      );
    }
    user_phone &&
      Linking.openURL(
        `whatsapp://send?text=${share_message}&phone=+91${user_phone}`,
      );
    analytics.trackEvent(MY_ORDERS_EVENTS.ORDER_SHARE, {
      order_id,
    });
  };

  const navigateToDetails = (order_id, source, isVacationOrder) => {
    dispatch(
      orderActions.setSelectedOrder({
        orderId: parseInt(order_id, 10),
        isVacationOrder,
        source,
      }),
    );
    navigation.navigate("OrderDetails");

    analytics.trackEvent(MY_ORDERS_EVENTS.ORDER_CARD_CLICK, {
      screen_name: params?.screen_name,
      order_id,
    });
  };

  const navigateToRating = (navParams) => {
    navigation.navigate("RateOrder", navParams);
  };

  const loadMore = () => {
    if (isOrders && !endReached && !loading) {
      dispatch(
        orderActions.getMoreOrdersReq({
          offset: allOrders.length,
          query,
        }),
      );
    } else if (
      !isOrders &&
      allReturnOrders &&
      allReturnOrders.length < totalReturnOrderCount &&
      !returnOrdersloading
    ) {
      dispatch(
        returnOrderActions.fetchMoreReturnOrdersRequest({
          offset: allReturnOrders.length,
          query,
        }),
      );
    }
  };

  const refreshControl = () => {
    if (isOrders) {
      dispatch(orderActions.getAllOrdersReq());
    } else {
      dispatch(returnOrderActions.fetchReturnOrderListRequest());
    }
    setQuery("");
  };

  const ListFooterComponent = () => {
    return (isOrders &&
      allOrders.length < totalCount &&
      !loading &&
      !params.fromShop) ||
      (!isOrders &&
        allReturnOrders &&
        allReturnOrders.length < totalReturnOrderCount) ? (
      <View style={{ paddingVertical: 10, backgroundColor: "white" }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    ) : null;
  };

  return (
    <View style={{ flex: 1 }} testID={testIds.OrdersList}>
      <CustomModal
        visible={!!modalValues}
        heading={modalValues?.heading}
        message={modalValues?.message}
        buttons={modalValues?.buttons}
        onPress={modalValues?.onPress}
      />
      <View style={styles.header}>
        <View style={styles.paddingHorizontal}>
          <SearchBar
            query={query}
            placeholder={t(`${PATH}/searchOrders`)}
            handleQueryChange={(value) => {
              setQuery(value);
            }}
            shouldShowIcon={true}
            rightIcon={!query ? <></> : <CloseIcon />}
            clearSearch={true}
            onPressRightIcon={() => {
              setQuery("");
            }}
            backBtn
          />
        </View>
      </View>

      <View style={styles.tabView}>
        <TouchableOpacity
          testID="ol-orders-tab"
          style={[
            styles.innerTabView,
            {
              borderBottomColor: isOrders
                ? colors.primary
                : colors.lighterBlack,
              borderBottomWidth: isOrders ? 2 : 1,
            },
          ]}
          onPress={() => setIsOrders(true)}
        >
          <Text
            style={{
              fontFamily: isOrders
                ? "NotoSansDevanagari-SemiBold"
                : "NotoSansDevanagari-Regular",
              fontSize: isOrders ? normalize(16.5) : normalize(16),
              color: isOrders ? colors.primary : colors.mediumBlack,
            }}
          >
            ऑर्डर्स
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="ol-return-tab"
          style={[
            styles.innerTabView,
            {
              borderBottomColor: !isOrders
                ? colors.primary
                : colors.lighterBlack,
              borderBottomWidth: !isOrders ? 2 : 1,
            },
          ]}
          onPress={() => setIsOrders(false)}
        >
          <Text
            style={{
              fontFamily: !isOrders
                ? "NotoSansDevanagari-SemiBold"
                : "NotoSansDevanagari-Regular",
              fontSize: !isOrders ? normalize(16.5) : normalize(16),
              color: !isOrders ? colors.primary : colors.mediumBlack,
            }}
          >
            रिटर्न
          </Text>
        </TouchableOpacity>
      </View>
      {loading ? (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          testID={testIds.order_list}
          data={isOrders ? ordersList : allReturnOrders}
          renderItem={_renderItem}
          keyExtractor={keyExtractor}
          refreshControl={
            <RefreshControl
              refreshing={loading || returnOrdersloading}
              onRefresh={refreshControl}
            />
          }
          contentContainerStyle={{ marginTop: 5, paddingBottom: 60 }}
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={
            !loading &&
            (allOrders?.length === 0 || allReturnOrders?.length === 0) ? (
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  paddingVertical: metrics.screenHeight * 0.125,
                }}
              >
                <NoOrdersGraphic />
                <Text
                  fontType="content1"
                  style={{
                    color: colors.lightBlack,
                    textAlign: "center",
                    paddingTop: 20,
                  }}
                >
                  {t(`${PATH}/noOrders`)}
                </Text>
              </View>
            ) : null
          }
          ListFooterComponent={ListFooterComponent}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    backgroundColor: "white",
    paddingBottom: 10,
  },
  paddingHorizontal: {
    paddingTop: 10,
    paddingHorizontal: 15,
  },
  tabView: {
    flexDirection: "row",
    paddingTop: normalize(16),
    marginHorizontal: normalize(16),
    borderBottomWidth: 1,
    borderBottomColor: colors.lighterBlack,
  },
  innerTabView: {
    width: "50%",
    justifyContent: "center",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.lighterBlack,
  },
});

export default OrderList;
